import { appendFile, mkdir } from 'node:fs/promises';
import { dirname } from 'node:path';
import type OpenAI from 'openai';
import {
  type ChatCompletionMessageParam,
  type ChatCompletionTool,
} from 'openai/resources/chat/completions';
import type { CompletionUsage } from 'openai/resources/completions';
import type {
  ResponseFormatJSONObject,
  ResponseFormatJSONSchema,
  ResponseFormatText,
} from 'openai/resources/shared';
import type { Stream } from 'openai/streaming';
import { UserError } from '../../errors';
import logger from '../../logger';
import type {
  Model,
  ModelMetadata,
  ModelRequest,
  ModelResponse,
  ModelRetryAdvice,
  ModelRetryAdviceRequest,
  SerializedHandoff,
  SerializedOutputType,
} from '../../model';
import { resetCurrentSpan, setCurrentSpan } from '../../tracing/context';
import {
  createGenerationSpan,
  withGenerationSpan,
} from '../../tracing/create-spans';
import { type GenerationSpanData, Span } from '../../tracing/spans';
import type { ResponseStreamEvent } from '../../types/helpers';
import * as protocol from '../../types/protocol';
import { Usage } from '../../usage';
import {
  getCompatibleToolChoice,
  itemsToMessages,
  toolToOpenAI,
} from './chat-completions-converter';
import { HEADERS } from './defaults';
import { convertChatCompletionsStreamToResponses } from './openai-chat-completions-streaming';
import { getOpenAIRetryAdvice } from './retry-advice';

export const FAKE_ID = 'FAKE_ID';

const RAW_PROVIDER_DEBUG_FILE = '/tmp/lyra-provider-raw.ndjson';

export type OpenAIChatCompletionsRequestData =
  | (OpenAI.Chat.Completions.ChatCompletionCreateParamsStreaming &
      Record<string, unknown>)
  | (OpenAI.Chat.Completions.ChatCompletionCreateParamsNonStreaming &
      Record<string, unknown>);

export type OpenAIChatCompletionsModelOptions = {
  itemsToMessages?: (
    items: ModelRequest['input'],
  ) => ChatCompletionMessageParam[];
  prepareRequestData?: (args: {
    requestData: OpenAIChatCompletionsRequestData;
    request: ModelRequest;
    stream: boolean;
    model: string;
    tools: ChatCompletionTool[];
  }) => OpenAIChatCompletionsRequestData | void;
};

// Some Chat Completions API compatible providers return a reasoning property on the message
// If that's the case we handle them separately
type OpenAIMessageWithReasoning =
  OpenAI.Chat.Completions.ChatCompletionMessage & {
    reasoning?: string;
    reasoning_content?: string;
  };

function getReasoningContent(
  message: OpenAI.Chat.Completions.ChatCompletionMessage,
): string | undefined {
  const maybeReasoningMessage = message as OpenAIMessageWithReasoning;
  if (
    typeof maybeReasoningMessage.reasoning === 'string' &&
    maybeReasoningMessage.reasoning
  ) {
    return maybeReasoningMessage.reasoning;
  }
  if (
    typeof maybeReasoningMessage.reasoning_content === 'string' &&
    maybeReasoningMessage.reasoning_content
  ) {
    return maybeReasoningMessage.reasoning_content;
  }
  return undefined;
}

/**
 * A model that uses (or is compatible with) OpenAI's Chat Completions API.
 */
export class OpenAIChatCompletionsModel implements Model {
  #client: OpenAI;
  #model: string;
  #options: OpenAIChatCompletionsModelOptions;
  metadata?: ModelMetadata;

  constructor(
    client: OpenAI,
    model: string,
    metadata?: ModelMetadata,
    options: OpenAIChatCompletionsModelOptions = {},
  ) {
    this.#client = client;
    this.#model = model;
    this.metadata = metadata;
    this.#options = options;
  }

  getRetryAdvice(args: ModelRetryAdviceRequest): ModelRetryAdvice | undefined {
    return getOpenAIRetryAdvice(args);
  }

  async getResponse(request: ModelRequest): Promise<ModelResponse> {
    const fetchResponse = async (
      span: Span<GenerationSpanData> | undefined,
    ) => {
      if (!span) return this.#fetchResponse(request, span, false);

      span.spanData.model = this.#model;
      span.spanData.model_config = request.modelSettings
        ? {
            temperature: request.modelSettings.temperature,
            top_p: request.modelSettings.topP,
            frequency_penalty: request.modelSettings.frequencyPenalty,
            presence_penalty: request.modelSettings.presencePenalty,
            reasoning_effort: request.modelSettings.reasoning?.effort,
          }
        : { base_url: this.#client.baseURL };
      const response = await this.#fetchResponse(request, span, false);
      if (span && request.tracing === true) {
        span.spanData.output = [response];
      }
      return response;
    };

    const response = request.tracing
      ? await withGenerationSpan(fetchResponse)
      : await fetchResponse(undefined);

    const output: protocol.OutputModelItem[] = [];
    if (response.choices && response.choices[0]) {
      const message = response.choices[0].message;
      const reasoning = getReasoningContent(message);

      if (reasoning !== undefined) {
        output.push({
          type: 'reasoning',
          content: [],
          rawContent: [
            {
              type: 'reasoning_text',
              text: reasoning,
            },
          ],
        });
      }
      const hasContent =
        message.content !== undefined &&
        message.content !== null &&
        // Azure OpenAI returns empty string instead of null for tool calls, causing parser rejection
        !(message.tool_calls && message.content === '');

      if (hasContent) {
        const { content, ...rest } = message;
        output.push({
          id: response.id,
          type: 'message',
          role: 'assistant',
          content: [
            {
              type: 'output_text',
              text: content || '',
              providerData: rest,
            },
          ],
          status: 'completed',
        });
      } else if (message.refusal) {
        const { refusal, ...rest } = message;
        output.push({
          id: response.id,
          type: 'message',
          role: 'assistant',
          content: [
            {
              type: 'refusal',
              refusal: refusal || '',
              providerData: rest,
            },
          ],
          status: 'completed',
        });
      }

      if (message.tool_calls) {
        for (const tool_call of message.tool_calls) {
          if (tool_call.type === 'function') {
            // Note: custom tools are not supported for now
            const { id: callId, ...remainingToolCallData } = tool_call;
            const {
              arguments: args,
              name,
              ...remainingFunctionData
            } = tool_call.function;
            output.push({
              id: response.id,
              type: 'function_call',
              arguments: args,
              name: name,
              callId: callId,
              status: 'completed',
              providerData: {
                ...remainingToolCallData,
                ...remainingFunctionData,
                ...(reasoning !== undefined
                  ? { reasoning_content: reasoning }
                  : {}),
              },
            });
          }
        }
      }
    }
    const modelResponse: ModelResponse = {
      usage: response.usage
        ? new Usage(toResponseUsage(response.usage))
        : new Usage(),
      output,
      responseId: response.id,
      providerData: response,
    };

    return modelResponse;
  }

  async *getStreamedResponse(
    request: ModelRequest,
  ): AsyncIterable<ResponseStreamEvent> {
    const span = request.tracing ? createGenerationSpan() : undefined;
    try {
      if (span) {
        span.start();
        setCurrentSpan(span);
      }
      const stream = await this.#fetchResponse(request, span, true);

      const response: OpenAI.Chat.Completions.ChatCompletion = {
        id: FAKE_ID,
        created: Math.floor(Date.now() / 1000),
        model: this.#model,
        object: 'chat.completion',
        choices: [],
        usage: {
          prompt_tokens: 0,
          completion_tokens: 0,
          total_tokens: 0,
        },
      };
      for await (const event of convertChatCompletionsStreamToResponses(
        response,
        stream,
      )) {
        if (
          event.type === 'response_done' &&
          response.usage?.total_tokens === 0
        ) {
          response.usage = {
            prompt_tokens: event.response.usage.inputTokens,
            completion_tokens: event.response.usage.outputTokens,
            total_tokens: event.response.usage.totalTokens,
            prompt_tokens_details: Array.isArray(
              event.response.usage.inputTokensDetails,
            )
              ? event.response.usage.inputTokensDetails[0]
              : event.response.usage.inputTokensDetails,
            completion_tokens_details: Array.isArray(
              event.response.usage.outputTokensDetails,
            )
              ? event.response.usage.outputTokensDetails[0]
              : event.response.usage.outputTokensDetails,
          };
        }
        yield event;
      }

      if (span && response && request.tracing === true) {
        span.spanData.output = [response];
      }
    } catch (error) {
      if (span) {
        span.setError({
          message: 'Error streaming response',
          data: {
            error:
              request.tracing === true
                ? String(error)
                : error instanceof Error
                  ? error.name
                  : undefined,
          },
        });
      }
      throw error;
    } finally {
      if (span) {
        span.end();
        resetCurrentSpan();
      }
    }
  }

  /**
   * @internal
   */
  async #fetchResponse(
    request: ModelRequest,
    span: Span<GenerationSpanData> | undefined,
    stream: true,
  ): Promise<Stream<OpenAI.Chat.Completions.ChatCompletionChunk>>;
  async #fetchResponse(
    request: ModelRequest,
    span: Span<GenerationSpanData> | undefined,
    stream: false,
  ): Promise<OpenAI.Chat.Completions.ChatCompletion>;
  async #fetchResponse(
    request: ModelRequest,
    span: Span<GenerationSpanData> | undefined,
    stream: boolean,
  ): Promise<
    | Stream<OpenAI.Chat.Completions.ChatCompletionChunk>
    | OpenAI.Chat.Completions.ChatCompletion
  > {
    const tools: OpenAI.Chat.Completions.ChatCompletionTool[] = [];
    if (request.tools) {
      for (const tool of request.tools) {
        if (tool.type === 'function') {
          if (
            typeof tool.namespace === 'string' &&
            tool.namespace.trim().length > 0
          ) {
            throw new UserError(
              'Namespaced function tools created with toolNamespace() are only supported with the Responses API.',
            );
          }
        }
        tools.push(toolToOpenAI(tool));
      }
    }
    if (request.handoffs) {
      for (const handoff of request.handoffs) {
        tools.push(convertHandoffTool(handoff));
      }
    }
    const responseFormat = getResponseFormat(request.outputType);

    let parallelToolCalls: boolean | undefined;
    if (typeof request.modelSettings.parallelToolCalls === 'boolean') {
      if (request.modelSettings.parallelToolCalls && tools.length === 0) {
        throw new Error('Parallel tool calls are not supported without tools');
      }

      parallelToolCalls = request.modelSettings.parallelToolCalls;
    }

    const messages = (this.#options.itemsToMessages ?? itemsToMessages)(
      request.input,
    );
    if (request.systemInstructions) {
      messages.unshift({
        content: request.systemInstructions,
        role: 'system',
      });
    }

    if (span && request.tracing === true) {
      span.spanData.input = messages;
    }

    const providerData = request.modelSettings.providerData ?? {};
    if (
      request.modelSettings.reasoning &&
      request.modelSettings.reasoning.effort
    ) {
      // merge the top-level reasoning.effort into provider data
      providerData.reasoning_effort = request.modelSettings.reasoning.effort;
    }
    // if (request.modelSettings.text && request.modelSettings.text.verbosity) {
    //   // merge the top-level text.verbosity into provider data
    //   providerData.verbosity = request.modelSettings.text.verbosity;
    // }

    let requestData: OpenAIChatCompletionsRequestData = {
      model: this.#model,
      messages,
      tools: tools.length ? tools : undefined,
      temperature: request.modelSettings.temperature,
      top_p: request.modelSettings.topP,
      frequency_penalty: request.modelSettings.frequencyPenalty,
      presence_penalty: request.modelSettings.presencePenalty,
      max_tokens: request.modelSettings.maxTokens,
      tool_choice: getCompatibleToolChoice(
        request.modelSettings.toolChoice,
        tools,
      ),
      parallel_tool_calls: parallelToolCalls,
      stream: stream ? true : false,
      stream_options: stream ? { include_usage: true } : undefined,
      store: request.modelSettings.store,
      prompt_cache_retention: request.modelSettings.promptCacheRetention,
      ...providerData,
    };

    if (responseFormat) {
      requestData.response_format = responseFormat;
    }

    requestData =
      this.#options.prepareRequestData?.({
        requestData,
        request,
        stream,
        model: this.#model,
        tools,
      }) ?? requestData;

    if (logger.dontLogModelData) {
      logger.debug('Calling LLM');
    } else {
      logger.debug(
        `Calling LLM. Request data: ${JSON.stringify(requestData, null, 2)}`,
      );
    }

    const rawProviderDebugId = createRawProviderDebugId();
    await writeRawProviderDebug({
      type: 'request',
      requestId: rawProviderDebugId,
      model: this.#model,
      stream,
      baseURL: String(this.#client.baseURL),
      requestData,
    });

    const requestOptions: {
      headers: typeof HEADERS;
      signal: AbortSignal | undefined;
      maxRetries?: number;
    } = {
      headers: HEADERS,
      signal: request.signal,
    };
    if (
      (
        request as ModelRequest & {
          _internal?: { runnerManagedRetry?: boolean };
        }
      )._internal?.runnerManagedRetry === true
    ) {
      requestOptions.maxRetries = 0;
    }

    let completion:
      | Stream<OpenAI.Chat.Completions.ChatCompletionChunk>
      | OpenAI.Chat.Completions.ChatCompletion;
    try {
      completion = await this.#client.chat.completions.create(
        requestData,
        requestOptions,
      );
    } catch (error) {
      await writeRawProviderDebug({
        type: 'error',
        requestId: rawProviderDebugId,
        model: this.#model,
        stream,
        error,
      });
      throw error;
    }

    if (logger.dontLogModelData) {
      logger.debug('Response received');
    } else {
      logger.debug(`Response received: ${JSON.stringify(completion, null, 2)}`);
    }

    if (stream) {
      await writeRawProviderDebug({
        type: 'stream_opened',
        requestId: rawProviderDebugId,
        model: this.#model,
      });
      return captureRawProviderStream(
        completion as Stream<OpenAI.Chat.Completions.ChatCompletionChunk>,
        rawProviderDebugId,
        this.#model,
      ) as Stream<OpenAI.Chat.Completions.ChatCompletionChunk>;
    }

    await writeRawProviderDebug({
      type: 'response',
      requestId: rawProviderDebugId,
      model: this.#model,
      response: completion,
    });
    return completion;
  }
}

function createRawProviderDebugId(): string {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function writeRawProviderDebug(
  entry: Record<string, unknown>,
): Promise<void> {
  try {
    await mkdir(dirname(RAW_PROVIDER_DEBUG_FILE), { recursive: true });
    await appendFile(
      RAW_PROVIDER_DEBUG_FILE,
      `${JSON.stringify({ timestamp: new Date().toISOString(), ...entry }, rawProviderDebugReplacer)}\n`,
      'utf8',
    );
  } catch {
    // Temporary debug logging must not alter model execution.
  }
}

async function* captureRawProviderStream(
  stream: Stream<OpenAI.Chat.Completions.ChatCompletionChunk>,
  requestId: string,
  model: string,
): AsyncIterable<OpenAI.Chat.Completions.ChatCompletionChunk> {
  let chunkIndex = 0;
  try {
    for await (const chunk of stream) {
      await writeRawProviderDebug({
        type: 'stream_chunk',
        requestId,
        model,
        chunkIndex,
        chunk,
      });
      chunkIndex++;
      yield chunk;
    }
    await writeRawProviderDebug({
      type: 'stream_end',
      requestId,
      model,
      chunkCount: chunkIndex,
    });
  } catch (error) {
    await writeRawProviderDebug({
      type: 'stream_error',
      requestId,
      model,
      chunkCount: chunkIndex,
      error,
    });
    throw error;
  }
}

function rawProviderDebugReplacer(_key: string, value: unknown): unknown {
  if (typeof value === 'bigint') {
    return value.toString();
  }
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }
  return value;
}

function toResponseUsage(
  usage: CompletionUsage,
): OpenAI.Responses.ResponseUsage & { requests: number } {
  return {
    requests: 1,
    input_tokens: usage.prompt_tokens,
    output_tokens: usage.completion_tokens,
    total_tokens: usage.total_tokens,
    input_tokens_details: {
      cached_tokens: usage.prompt_tokens_details?.cached_tokens || 0,
    },
    output_tokens_details: {
      reasoning_tokens: usage.completion_tokens_details?.reasoning_tokens || 0,
    },
  };
}

export function convertHandoffTool(
  handoff: SerializedHandoff,
): ChatCompletionTool {
  return {
    type: 'function',
    function: {
      name: handoff.toolName,
      description: handoff.toolDescription || '',
      parameters: handoff.inputJsonSchema,
    },
  };
}

function getResponseFormat(
  outputType: SerializedOutputType,
):
  | ResponseFormatText
  | ResponseFormatJSONSchema
  | ResponseFormatJSONObject
  | undefined {
  if (outputType === 'text') {
    // Avoid sending response_format for plain text responses because some Chat Completions
    // compatible providers (e.g., Claude) reject non-json_schema values here. OpenAI's API
    // already treats text as the default when the field is omitted.
    return undefined;
  }

  if (outputType.type === 'json_schema') {
    return {
      type: 'json_schema',
      json_schema: {
        name: outputType.name,
        strict: outputType.strict,
        schema: outputType.schema,
      },
    };
  }

  return { type: 'json_object' };
}
